import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  ChevronDown,
  Search,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Check,
} from "lucide-react";
import defaultLogo from "../../JMIlogo.png";

const ProductForm = ({
  registeredProductList = [],
  selectedRegisteredProductId = "",
  handleSelectRegisteredProduct,
  loadingRegistrations = false,
  selectedProduct = null,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  console.log(registeredProductList);
  const selectedProdItem = useMemo(() => {
    return (
      registeredProductList.find((p) => p.id === selectedRegisteredProductId) ||
      null
    );
  }, [registeredProductList, selectedRegisteredProductId]);

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return registeredProductList;
    const q = searchQuery.toLowerCase();
    return registeredProductList.filter(
      (p) =>
        p.productName?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.styleType?.toLowerCase().includes(q) ||
        p.specification?.toLowerCase().includes(q) ||
        p.productSource?.toLowerCase().includes(q) ||
        p.purity?.toLowerCase().includes(q),
    );
  }, [registeredProductList, searchQuery]);

  const getCategoryStyleSpecText = (prod) => {
    const parts = [];
    const segCat = `${prod.segment || ""}${" "}${prod.productSource} ${
      prod.category || ""
    }`.trim();
    if (segCat) parts.push(segCat);
    if (prod.styleType) parts.push(prod.styleType);
    if (prod.specification) parts.push(prod.specification);
    return parts.join(" / ");
  };

  const getSpecificationText = (prod) => {
    const aboveParts = [];
    const belowParts = [];

    // above: Set MC and Net Gram MC
    if (prod.setMC && prod.setMC !== "0" && prod.setMC !== "") {
      aboveParts.push(`Set MC: ${prod.setMC}`);
    }
    if (prod.netGramMC && prod.netGramMC !== "0" && prod.netGramMC !== "") {
      aboveParts.push(`Net Gram MC: ${prod.netGramMC}`);
    }

    // below: specification MC and gram rate
    if (
      prod.specificationMC &&
      prod.specificationMC !== "0" &&
      prod.specificationMC !== ""
    ) {
      belowParts.push(
        `${prod.specification || ""} Wesatge: ${prod.specificationMC}%`,
      );
    }
    if (
      prod.specificationGramRate &&
      prod.specificationGramRate !== "0" &&
      prod.specificationGramRate !== ""
    ) {
      belowParts.push(
        `${prod.specification || ""} Gram Rate: ${prod.specificationGramRate}`,
      );
    }

    // wastage shown inline next to purity
    const wastage =
      prod.wastage && prod.wastage !== "0" && prod.wastage !== ""
        ? `Wastage: ${prod.wastage}%`
        : "";

    return {
      above: aboveParts.join(" | "),
      below: belowParts.join(" | "),
      wastage,
    };
  };

  return (
    <div className="section product-info-section">
      <div
        className="section-header-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "14px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <h3 className="section-title" style={{ margin: 0 }}>
          Registered Product
        </h3>
        {registeredProductList.length > 0 ? (
          <span
            className="registered-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "12px",
              background: "#ecfdf5",
              color: "#047857",
              padding: "4px 10px",
              borderRadius: "16px",
              fontWeight: 600,
              border: "1px solid #a7f3d0",
            }}
          >
            <CheckCircle2 size={14} color="#059669" />{" "}
            {registeredProductList.length} Approved Product
            {registeredProductList.length > 1 ? "s" : ""}
          </span>
        ) : (
          !loadingRegistrations && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "12px",
                background: "#fef2f2",
                color: "#b91c1c",
                padding: "4px 10px",
                borderRadius: "16px",
                fontWeight: 500,
                border: "1px solid #fecaca",
              }}
            >
              <AlertTriangle size={14} color="#dc2626" /> No Approved Products
            </span>
          )
        )}
      </div>

      <div className="registered-dropdown-container" ref={dropdownRef}>
        {/* Trigger (shows selected product or placeholder) */}
        <div
          className="registered-product-trigger"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {selectedProdItem ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "10px",
              }}
            >
              <div
                className="registered-item-card"
                style={{ padding: 0, background: "transparent" }}
              >
                <div className="registered-item-img-wrapper">
                  <img
                    src={selectedProdItem.image || defaultLogo}
                    alt={selectedProdItem.productName}
                    className="registered-item-img"
                    onError={(e) => {
                      e.target.src = defaultLogo;
                    }}
                  />
                </div>
                <div className="registered-item-content">
                  <h4 className="registered-item-name">
                    {selectedProdItem.productName}
                  </h4>
                  <div className="registered-item-sub">
                    {getCategoryStyleSpecText(selectedProdItem)}
                  </div>
                  {(() => {
                    const specs = getSpecificationText(selectedProdItem);
                    return (
                      <>
                        <div className="registered-item-purity">
                          Purity :{selectedProdItem.purity || "N/A"}
                          {specs.wastage && (
                            <span style={{ marginLeft: "10px" }}>
                              {specs.wastage}
                            </span>
                          )}
                        </div>
                        {specs.above && (
                          <div className="registered-item-specs">
                            {specs.above}
                          </div>
                        )}
                        {specs.below && (
                          <div className="registered-item-specs">
                            {specs.below}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <ChevronDown
                size={20}
                color="#94a3b8"
                style={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  flexShrink: 0,
                }}
              />
            </div>
          ) : (
            <div className="registered-trigger-placeholder">
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#047857",
                  borderColor: "1px solid #047857",
                }}
              >
                <Sparkles size={16} color="#d97706" />
                {loadingRegistrations
                  ? "Loading registered products..."
                  : registeredProductList.length > 0
                  ? "Choose Registered Approved Product"
                  : "No approved registered products available"}
              </span>
              <ChevronDown
                size={18}
                color="#94a3b8"
                style={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </div>
          )}
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="registered-dropdown-menu">
            {registeredProductList.length > 4 && (
              <div className="registered-search-box">
                <Search size={16} className="registered-search-icon" />
                <input
                  type="text"
                  placeholder="Search registered products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="registered-search-input"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            <div className="registered-options-list">
              {filteredProducts.length === 0 ? (
                <div
                  style={{
                    padding: "24px 16px",
                    textAlign: "center",
                    color: "#94a3b8",
                    fontSize: "13px",
                  }}
                >
                  {registeredProductList.length === 0
                    ? "No approved registered products found. Please register products and wait for approval."
                    : "No products matching your search."}
                </div>
              ) : (
                filteredProducts.map((prod) => {
                  const isSelected = prod.id === selectedRegisteredProductId;
                  return (
                    <div
                      key={prod.id}
                      className={`registered-item-card ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() => {
                        handleSelectRegisteredProduct(prod.id);
                        setIsOpen(false);
                      }}
                    >
                      <div className="registered-item-img-wrapper">
                        <img
                          src={prod.image || defaultLogo}
                          alt={prod.productName}
                          className="registered-item-img"
                          onError={(e) => {
                            e.target.src = defaultLogo;
                          }}
                        />
                      </div>
                      <div className="registered-item-content">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <h4 className="registered-item-name">
                            {prod.productName}
                          </h4>
                          {isSelected && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                fontSize: "11px",
                                color: "#10b981",
                                fontWeight: 600,
                              }}
                            >
                              <Check size={14} /> Selected
                            </span>
                          )}
                        </div>
                        <div className="registered-item-sub">
                          {getCategoryStyleSpecText(prod)}
                        </div>
                        {(() => {
                          const specs = getSpecificationText(prod);
                          return (
                            <>
                              <div className="registered-item-purity">
                                Purity :{prod.purity || "N/A"}{" "}
                                {specs.wastage && <span>{specs.wastage}</span>}
                              </div>
                              {specs.above && (
                                <div className="registered-item-specs">
                                  {specs.above}
                                </div>
                              )}
                              {specs.below && (
                                <div className="registered-item-specs">
                                  {specs.below}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductForm;
